// Row order for the Tank Planning grid — shared by the staff planner and the
// customer-portal Tank Schedule so both list tanks the same way.

interface SortableVessel {
  name: string;
  vessel_subtypes?: string[] | null;
  capacity_l: number | null;
  capacity_tons: number | null;
}

// Sort groups:
//   0 P*        — small storage/press tanks (year-round)
//   1 B (not BC) — large storage/bottling tanks (year-round)
//   2 L storage  — L tanks whose subtypes include Storage or Ferm-Closed-Top
//   3 BC*        — Butch Cameron rental tanks (harvest seasonal)
//   4 OT*        — open-top fermenters (harvest seasonal); L OT tanks land here too
//   5 other
//
// L tanks are classified by vessel_subtypes first (number < 10 as fallback).
// OT tanks keep their group even if a Storage subtype is later added.
export function vesselSortGroup(vessel: { name: string; vessel_subtypes?: string[] | null }): number {
  const name = vessel.name;
  const subtypes = vessel.vessel_subtypes ?? [];
  if (/^P/i.test(name)) return 0;
  if (/^BC/i.test(name)) return 3;
  if (/^B/i.test(name)) return 1;
  if (/^L/i.test(name)) {
    const isStorage = subtypes.some(s => s === 'Storage' || s === 'Ferm — Closed Top');
    if (isStorage) return 2;
    const isOT = subtypes.some(s => s === 'Ferm — Open Top');
    if (isOT) return 4;
    // Fallback: L1–L9 → storage, L10+ → OT
    const num = parseInt(name.replace(/^L/i, ''), 10);
    return !isNaN(num) && num < 10 ? 2 : 4;
  }
  if (/^OT/i.test(name)) return 4;
  return 5;
}

/** Sort by group, then capacity ascending (tons converted at ~150 gal/ton), then name. */
export function sortPlannerVessels<T extends SortableVessel>(vessels: T[]): T[] {
  return [...vessels].sort((a, b) => {
    const ga = vesselSortGroup(a);
    const gb = vesselSortGroup(b);
    if (ga !== gb) return ga - gb;
    const aVol = a.capacity_l ?? ((a.capacity_tons ?? 0) * 150);
    const bVol = b.capacity_l ?? ((b.capacity_tons ?? 0) * 150);
    if (aVol !== bVol) return aVol - bVol;
    return a.name.localeCompare(b.name);
  });
}
